import { useCallback } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button/Button';

import SmartCoverRequestAvatar from 'features/scheduleBuilder/ScheduleBuilderView/CoverRequests/SmartCoverRequestAvatar';
import SmartCoverRequestInsights from 'features/scheduleBuilder/ScheduleBuilderView/CoverRequests/SmartCoverRequestInsights';
import {
  Reason,
  ReasonKey,
} from 'features/scheduleBuilder/slices/reassignmentRecommendations/types';

import { df, moment } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import { getCoverRequestReasonOptions } from '../helpers';

const I18N_PATH = 'my_week.user.shift_trade_requests.smart_cover_requests';

export interface RequestNotificationItem {
  personal: null;
  shift: {
    id: number;
    unscheduled: boolean;
    start_at: string;
    end_at: string;
    role_name: string | null;
  };
  totals: {
    paid_time_off_hours: number;
    holiday_pay: number;
    seventh_day_overtime_15: number;
    scheduled_spread_of_hours: number;
    overtime: number;
    regular_costs: number;
    double_overtimes: number;
    cash_tips: number;
    scheduled_paid_breaks_hours: number;
    blue_laws_hours: number;
    costs: number;
    daily_overtime: number;
    paid_ffcra_care_for_child_hours: number;
    overtime_costs: number;
    double_overtimes_cost: number;
    break_penalty: number;
    scheduled_seventh_day_overtime_20: number;
    scheduled_regular: number;
    ffcra_care_for_others_hours: number;
    paid_sick_time_hours: number;
    scheduled_overtime_rate: number | null;
    scheduled_unpaid_breaks_hours: number;
    actual_vs_scheduled_hours: number;
    paid: number;
    ffcra_paid_sick_leave_hours: number;
    scheduled_weekly_overtime: number;
    time_off_hours: number;
    split_shift_hours: number;
    actual: number;
    scheduled_seventh_day_overtime_15: number;
    scheduled_holiday_pay: number;
    scheduled_regular_overtimes_cost: number;
    paid_sick_time_cost: number;
    variance: number;
    scheduled_regular_costs: number;
    wage_type: string | null;
    scheduled_overtime: number;
    total_tips: number;
    spread_of_hours: number;
    scheduled_costs: number;
    scheduled_blue_laws_hours: number;
    ffcra_care_for_child_hours: number;
    scheduled_double_overtimes_cost: number;
    scheduled_double_overtimes: number;
    paid_breaks_hours: number;
    wage_rate: number | null;
    paid_ffcra_care_for_others_hours: number;
    paid_ffcra_paid_sick_leave_hours: number;
    scheduled_daily_overtime: number;
    scheduled_overtime_costs: number;
    scheduled_split_shift_hours: number;
    seventh_day_overtime_20: number;
    paid_time_off_cost: number;
    wage_rates: Array<{
      rate: number;
      type: string;
    }>;
    regular: number;
    scheduled_hours: number;
    regular_overtimes_cost: number;
    sick_time_hours: number;
    credit_tips: number;
    overtime_rate: number | null;
    unpaid_breaks_hours: number;
    weekly_overtime: number;
  };
  insights: Array<Reason & { reason_key: ReasonKey }>;
  trade_shift: null;
  responder_name: null;
  unique_id: string;
  created_at: string;
  receiver_first_name: string;
  state: string;
  requester_last_name: string;
  location_name?: string;
  _id: number;
  type: string;
  id: number;
  receiver_name: string;
  requester_avatar_url: string;
  requirement: string;
  alert_type: string;
  job_id: number;
  requester_first_name: string;
  estimated_pay: string;
  reason?: string;
}

interface Props {
  item: RequestNotificationItem;
  includeLocation?: boolean;
  onApprove?: () => void;
  onDecline?: () => void;
}

const SmartCoverRequestFeedCard = ({
  item,
  includeLocation,
  onApprove,
  onDecline,
}: Props) => {
  const roleName = item.shift?.role_name || '';
  const shiftDate = item.shift?.start_at
    ? moment(item.shift?.start_at).format(df('pretty_short_day_no_year'))
    : '';
  const shiftTime = item.shift
    ? `${moment(item.shift.start_at).format(df('msg_time'))} - ${moment(
        item.shift.end_at
      ).format(df('msg_time'))}`
    : '';
  const locationName = item.location_name || '';
  const estimatedPay = item?.estimated_pay ? `$${item.estimated_pay}` : '';
  const requesterName = `${item.requester_first_name || ''} ${
    item.requester_last_name || ''
  }`.trim();
  const receiverName = `${item.receiver_name || ''}`.trim();
  const avatarUrl = item.requester_avatar_url || '';
  const insights = item.insights || [];

  const reasonOptions = getCoverRequestReasonOptions();
  const reasonKey = item.reason;
  const reasonValue = reasonOptions.find(option => option.value === reasonKey);
  const reasonForNeededCover = reasonValue ? reasonValue.label : reasonKey;
  const requirement = item.requirement || '';
  const isPending = item.state === 'pending';

  const handleAccept = useCallback(() => {
    onApprove?.();
  }, [onApprove]);

  const handleDecline = useCallback(() => {
    onDecline?.();
  }, [onDecline]);

  return (
    <Box bbw={1} bcolor="mono500" flex gap={8} column p={12}>
      <SmartCoverRequestAvatar
        avatarUrl={avatarUrl}
        requesterName={requesterName}
        createdAt={item.created_at}
      />

      <Box flex column gap={16}>
        {!isPending && (
          <Text variant="bodyBoldTight">
            {toI18n(`${I18N_PATH}.claimed_by`, {
              props: { name: receiverName },
            })}
          </Text>
        )}

        <Box flex column>
          <Text color="mono700" variant="bodySmBoldTight">
            {shiftDate}
          </Text>
          <Box flex gap={2} alignItemsCenter justifyItemsCenter>
            <Text color="mono700" variant="bodySmTight">
              {shiftTime}
            </Text>
            {roleName && (
              <Box flex gap={2}>
                <Text color="mono700" variant="bodySmTight">
                  &bull;
                </Text>
                <Text color="mono700" variant="bodySmTight">
                  {roleName}
                </Text>
              </Box>
            )}
          </Box>
        </Box>

        {includeLocation && locationName && (
          <Text variant="body">{locationName}</Text>
        )}

        <Box flex column>
          {estimatedPay && (
            <Box flex gap={4} alignItemsCenter>
              <Text color="mono700" variant="bodySmBoldTight">
                {toI18n(`${I18N_PATH}.estimated_pay`)}
              </Text>
              <Text color="mono700" variant="bodySmTight">
                {estimatedPay}
              </Text>
            </Box>
          )}

          {requirement && (
            <Box flex gap={4} alignItemsCenter>
              <Text color="mono700" variant="bodySmBoldTight">
                {toI18n(`${I18N_PATH}.qualifications`)}
              </Text>
              <Text color="mono700" variant="bodySmTight">
                {requirement}
              </Text>
            </Box>
          )}
          {reasonForNeededCover && (
            <Box flex gap={4} alignItemsCenter>
              <Text color="mono700" variant="bodySmBoldTight">
                {toI18n(`${I18N_PATH}.reason_for_needed_cover`)}
              </Text>
              <Text color="mono700" variant="bodySmTight">
                {reasonForNeededCover}
              </Text>
            </Box>
          )}
        </Box>

        {insights && <SmartCoverRequestInsights insights={insights} />}

        <Box alignEnd justifyContentEnd w="100%" flex h="100%" gap={8}>
          {onDecline && (
            <Button variant="secondary" onClick={handleDecline}>
              {toI18n(`${I18N_PATH}.${isPending ? 'decline' : 'deny_cover'}`)}
            </Button>
          )}
          {onApprove && (
            <Button variant="primary" onClick={handleAccept}>
              {toI18n(`${I18N_PATH}.${isPending ? 'accept' : 'approve_cover'}`)}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SmartCoverRequestFeedCard;
